"use client";

import { Heading, Icon } from "oddsgate-ds";
import {
  StyledLanguageSelector,
  StyledOverlay,
  StyledTrigger,
} from "./LanguageSwitcher.theme";

import Link from "next/link";
import { removeScroll } from "@/lib/helpers/scrollHelpers";
import { useGlobalContext } from "@/lib/globalContext";
import { usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import { getTranslatedRoutesBySlug } from "@/lib/actions/apiRequests";
import { LANG_PATH_PREFIX_REGEX } from "@/lib/helpers/langUtils";

type TranslatedRoute = {
  uri: string | null;
  slug: string | null;
  language: string;
};

const LanguageSwitcher = () => {
  const state = useGlobalContext();
  const [menuActive, setMenuActive] = useState(false);
  const [translatedRoutes, setTranslatedRoutes] = useState<Record<string, TranslatedRoute>>({});
  const pathname = usePathname();
  const defaultLang = process.env.NEXT_PUBLIC_DEFAULT_LANG || "en";

  const languages = state?.languages ?? [];
  const currentLang = state?.lang || defaultLang;

  // Extract current slug from pathname (remove language prefix)
  const getCurrentSlug = (): string => {
    let path = pathname;
    // Remove language prefix
    const langMatch = path.match(LANG_PATH_PREFIX_REGEX);
    if (langMatch) {
      path = path.slice(langMatch[0].length);
    }
    // Remove leading/trailing slashes and get the last segment (post slug)
    const segments = path
      .replace(/^\/|\/$/g, "")
      .split("/")
      .filter(Boolean);
    return segments[segments.length - 1] || "/";
  };

  // Fetch translated routes when pathname changes
  useEffect(() => {
    const slug = getCurrentSlug();
    if (!slug || slug === "/") {
      setTranslatedRoutes({});
      return;
    }
    getTranslatedRoutesBySlug(slug)
      .then(({ data }) => {
        if (data) setTranslatedRoutes(data as Record<string, TranslatedRoute>);
      })
      .catch(() => setTranslatedRoutes({}));
  }, [pathname]);

  const languageOnClick = (isActive: boolean) => {
    removeScroll(isActive, state);
    setMenuActive(isActive);
  };

  if (!languages || languages.length <= 1) {
    return <></>;
  }

  const buildLangUrl = (targetLang: string): string => {
    // Use translated route from WPML if available
    const translated = translatedRoutes[targetLang];
    if (translated?.uri) {
      // Extract path from full URI (strip domain)
      try {
        const url = new URL(translated.uri);
        const path = url.pathname.replace(/\/$/, "") || "/";
        // Strip WPML language prefix using string comparison (avoids regex injection)
        const prefix = `/${targetLang}`;
        const langStripped =
          path === prefix ? "/" :
          path.startsWith(`${prefix}/`) ? path.slice(prefix.length) : path;
        if (targetLang === defaultLang) {
          return langStripped || "/";
        } else {
          return `/${targetLang}${langStripped}`;
        }
      } catch (e) {
        console.warn("[LanguageSwitcher] Malformed translation URI:", translated?.uri, e);
      }
    }

    // No translation found → go to homepage of target language
    if (targetLang === defaultLang) {
      return "/";
    } else {
      return `/${targetLang}/`;
    }
  };

  return (
    <div className="d-inline-flex position-relative">
      <StyledTrigger
        variant="secondary"
        mode="light"
        $active={menuActive}
        rightIcon={<Icon icon="icon-arrowUp" />}
        onClick={() => languageOnClick(!menuActive)}
      >
        {state.dict?.custom?.language || "Language"}
      </StyledTrigger>

      <StyledLanguageSelector $active={menuActive} data-lenis-prevent={true}>
        <div>
          <ul className="">
            {languages?.map((lang: any) => {
              const langUrl = buildLangUrl(lang.code);
              return (
                <li
                  key={`locale-${lang.code}`}
                  className="mb-5 mt-md-1 mb-md-0"
                >
                  <Link href={langUrl} onClick={() => languageOnClick(false)}>
                    <Heading
                      tag="span"
                      size="h5"
                      className={`${currentLang === lang.code ? "color-primary50" : "color-third50"} fw-bold text-uppercase`}
                    >
                      {lang.name}
                    </Heading>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </StyledLanguageSelector>
      <StyledOverlay
        $active={menuActive}
        onClick={() => languageOnClick(false)}
      ></StyledOverlay>
    </div>
  );
};

export default LanguageSwitcher;
